import { isPlatformBrowser } from "@angular/common";
import { computed, inject, Injectable, PLATFORM_ID, signal } from "@angular/core";

import { environment } from "@azm/environments/environment";
import { chartThemeToken } from "./chart-theme.token";

export type Theme = "dark" | "light";

const LEGACY_STORAGE_KEY = "azm-theme";

@Injectable({
	providedIn: "root",
})
export class ThemeService {
	readonly #platformId = inject(PLATFORM_ID);
	readonly #storageKey = "azm-theme-int";

	readonly enabled = environment.enableThemeSwitch === true;
	readonly currentTheme = signal<Theme>(environment.defaultTheme === "dark" ? "dark" : "light");
	readonly isDark = computed(() => this.currentTheme() === "dark");

	initializeTheme(): void {
		if (!isPlatformBrowser(this.#platformId)) {
			return;
		}

		this.#apply(this.#resolveInitialTheme(), false);
	}

	toggleTheme(): void {
		if (!this.enabled || !isPlatformBrowser(this.#platformId)) {
			return;
		}

		this.#apply(this.currentTheme() === "dark" ? "light" : "dark", true);
	}

	#resolveInitialTheme(): Theme {
		if (!this.enabled) {
			return "light";
		}

		return this.#readSaved() ?? (environment.defaultTheme === "dark" ? "dark" : "light");
	}

	#apply(theme: Theme, persist: boolean): void {
		const root = document.documentElement;
		root.setAttribute("data-azm-theme", theme);
		root.style.colorScheme = theme;
		chartThemeToken.set(theme);
		this.currentTheme.set(theme);

		try {
			if (!this.enabled) {
				localStorage.removeItem(this.#storageKey);
				localStorage.removeItem(LEGACY_STORAGE_KEY);
				return;
			}

			if (persist) {
				localStorage.setItem(this.#storageKey, theme);
				localStorage.removeItem(LEGACY_STORAGE_KEY);
			}
		} catch {}
	}

	#readSaved(): Theme | null {
		try {
			const scoped = this.#parseTheme(localStorage.getItem(this.#storageKey));
			if (scoped) {
				return scoped;
			}

			return null;
		} catch {
			return null;
		}
	}

	#parseTheme(value: string | null): Theme | null {
		return value === "dark" || value === "light" ? value : null;
	}
}
